import get from 'lodash.get';
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import MuiTableHead from '@mui/material/TableHead';
import MuiTableBody from '@mui/material/TableBody';
import MuiTableRow from '@mui/material/TableRow';

import Text from '../BuildingBlocks/Text';

import { TableWrapper, StyledCell, StyledTable, StyledRow, StyledEmptyCell } from './Table.styles';

class Table extends Component {
  static propTypes = {
    /** Tell table to ignore certain object keys - 'id' gets ignored by default */
    blackList: PropTypes.arrayOf(PropTypes.string),
    /** Array of content objects to display in table */
    children: PropTypes.arrayOf(PropTypes.shape({})),
    /** Object with keys matching row object IDs containing @MUITableCell properties and valid td attributes */
    contentCellsConfig: PropTypes.shape({}),
    /** Object with keys matching row object IDs containing @MUITableCell properties and valid td attributes */
    headerCellsConfig: PropTypes.shape({}),
    /** Min width of the table passed via string (900px default) */
    minTableWidth: PropTypes.string,
    /** Array of label objects to display at the top of the table */
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
        width: PropTypes.string,
      })
    ).isRequired,
    showHeader: PropTypes.bool,
    rowStyles: PropTypes.shape({}),
    /** Style overrides for body rows only. Falls back to rowStyles when omitted. */
    bodyRowStyles: PropTypes.shape({}),
    /** Message for an empty row in the table */
    emptyRowMessage: PropTypes.string,
    /** The strong font weight for the header */
    strong: PropTypes.bool,
  };

  static defaultProps = {
    blackList: [],
    children: undefined,
    contentCellsConfig: {},
    headerCellsConfig: {},
    minTableWidth: '900px',
    showHeader: true,
    rowStyles: undefined,
    bodyRowStyles: undefined,
    emptyRowMessage: '',
    strong: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      globalBlackList: ['id'],
    };
    this.renderBody = this.renderBody.bind(this);
    this.renderHead = this.renderHead.bind(this);
  }

  componentDidMount() {
    const { blackList } = this.props;
    const { globalBlackList } = this.state;

    /** Add passed blackList items to globalBlackList array */
    const newBlackList = globalBlackList.concat(blackList);

    if (globalBlackList.length !== newBlackList.length) {
      this.setState({ globalBlackList: newBlackList });
    }
  }

  renderHead() {
    const { rows, showHeader, headerCellsConfig, rowStyles, strong } = this.props;

    return rows && showHeader ? (
      <MuiTableHead>
        <MuiTableRow style={rowStyles}>
          {rows.map(row => {
            const { id, label } = row;
            const cellConfigProps = get(headerCellsConfig, `${id}`, {});
            return (
              <StyledCell key={id} {...cellConfigProps}>
                {strong ? (
                  <Text.HeadingFifth noMargins align="center">
                    {label}
                  </Text.HeadingFifth>
                ) : (
                  <Text.CopyMini noMargins align="center">
                    {label}
                  </Text.CopyMini>
                )}
              </StyledCell>
            );
          })}
        </MuiTableRow>
      </MuiTableHead>
    ) : (
      undefined
    );
  }

  renderBody() {
    const {
      children,
      contentCellsConfig,
      rowStyles,
      bodyRowStyles,
      emptyRowMessage,
      rows,
    } = this.props;
    const { globalBlackList } = this.state;
    const resolvedBodyRowStyles = bodyRowStyles || rowStyles;

    return children ? (
      <MuiTableBody>
        {children && children.length === 0 ? (
          <StyledRow>
            <StyledCell colSpan={rows.length} align="center">
              <StyledEmptyCell>{emptyRowMessage}</StyledEmptyCell>
            </StyledCell>
          </StyledRow>
        ) : (
          children.map(child => (
            <StyledRow key={child.id} style={resolvedBodyRowStyles}>
              {Object.entries(child).map(([key, item]) => {
                let cell;
                if (!globalBlackList.includes(key)) {
                  const cellConfigProps = get(contentCellsConfig, `${key}`, {});
                  cell = (
                    <StyledCell key={key} {...cellConfigProps}>
                      {item}
                    </StyledCell>
                  );
                }
                return cell;
              })}
            </StyledRow>
          ))
        )}
      </MuiTableBody>
    ) : (
      undefined
    );
  }

  render() {
    const { minTableWidth } = this.props;
    return (
      <TableWrapper>
        <StyledTable minTableWidth={minTableWidth}>
          {this.renderHead()}
          {this.renderBody()}
        </StyledTable>
      </TableWrapper>
    );
  }
}

export default Table;
